/**
 * The hand: 24 px of hint plus one fan, bottom-anchored, fixed cost.
 *
 * Legal cards lift and keep full colour; illegal ones stay flat and muted. Both
 * are tappable only when legal — illegal taps are simply not wired, so there is
 * no error path for a rule the server already answered.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CardId, Suit } from '@cards/shared'
import { Fan, type FanCard } from './Fan'
import type { TableView } from './view'
import { useActionLock } from '../socket/actionLock'
import { useSettings, type HandOrder } from '../settings/settings'
import { buzz, BUZZ_TURN } from '../settings/haptics'
import { holdTilt } from './tilt'
import { coachAllowed, coachCard, useCoach } from '../coach/useCoach'
import { CoachLine } from '../coach/CoachLine'
import { ReferenceCard } from './ReferenceCard'

export interface HandBandProps {
  view: TableView
  /** Absent during the auction, when the hand is shown flat for reference. */
  onPlay?: (card: CardId, nominate?: Suit) => void
  interactive: boolean
  hint?: React.ReactNode
  locked?: boolean
  /** It is somebody else's turn to play. The hand is shaded until yours comes
   *  round, which is Trickster's loudest turn cue: every face-up card that is
   *  not playable right now sits under a scrim, so yours are dark while you
   *  wait and light up — the playable ones lifted — the moment it is your go.
   *  In the auction too, where it is lit only for your own call. */
  waiting?: boolean
}

export function HandBand({ view, onPlay, interactive, hint, locked, waiting }: HandBandProps): JSX.Element {
  const [sorted, setSorted] = useState(true)
  const [nominating, setNominating] = useState<{ card: CardId; suits: readonly Suit[]; coach?: Suit | null } | null>(null)
  const [settings, setSettings] = useSettings()
  const [guide, setGuide] = useState(false)
  const closeGuide = useCallback(() => setGuide(false), [])
  const RankGuide = view.gameUi.RankGuide
  const coach = useCoach(view)
  const ghost = interactive ? coachCard(coach) : null
  // The card just tapped, until the server answers. It rises out of the fan at
  // once, so the tap is seen to have done something across the round trip,
  // and the flight to the table then starts from where it rose to. The raw
  // gate, not the delayed one the buttons use: this is released by the very
  // answer that moves the card, so it never outlives the card being in hand —
  // and an answer that refuses the play drops it back into place.
  const inFlight = useActionLock()
  const [launching, setLaunching] = useState<CardId | null>(null)
  useEffect(() => {
    if (!inFlight) setLaunching(null)
  }, [inFlight])
  const play = (card: CardId, nominate?: Suit) => {
    setLaunching(card)
    onPlay?.(card, nominate)
  }
  useTurnBuzz(view)
  // Your hand catches the light in the auction too, where there is no felt.
  const band = useRef<HTMLElement | null>(null)
  useEffect(() => holdTilt(band.current), [])

  const cards = useMemo<FanCard[]>(() => {
    const order = sorted ? sortForDisplay(view, settings.handOrder) : view.myCards
    return order.map((card) => {
      const marks = view.gameUi.cardMarks(view, card)
      const legal = view.legalPlays.get(card)
      const playable = interactive && !!legal && !locked
      return {
        card,
        state: waiting ? 'waiting' : !interactive ? 'rest' : legal ? 'legal' : 'illegal',
        launching: card === launching,
        trump: marks.trump,
        stamp: marks.stamp,
        fromKitty: marks.fromKitty,
        coach: playable && ghost?.card === card,
        disabled: !playable,
        onClick: playable
          ? () => {
              // A floating Joker lead must name a suit; the server rejects a
              // play whose nomination is not in the list it sent.
              if (legal.nominate && legal.nominate.length > 0) {
                setNominating({ card, suits: legal.nominate, coach: ghost?.card === card ? ghost.nominate ?? null : null })
              } else {
                play(card)
              }
            }
          : undefined,
      }
    })
    // settings.deck is read by Fan; cardScale re-measures there. `play` is
    // rebuilt each render but only closes over `onPlay`, which is listed.
  }, [view, sorted, interactive, waiting, locked, onPlay, launching, settings.deck, settings.handOrder, ghost?.card])

  return (
    <section className="hand-band" ref={band}>
      <div className="hint">
        {/* On your turn the hint is your name tag: its "Your turn" wears the
            brass box the other seats' names wear on theirs. */}
        {coach ? (
          <CoachLine view={view} state={coach} />
        ) : (
          <div className={view.isMyTurn ? 'hint-text turn' : 'hint-text'}>{hint ?? view.gameUi.handHint(view)}</div>
        )}
        <div className="hint-btns">
          {coachAllowed(view) ? (
            <button
              className={settings.coach ? 'hint-btn coach-toggle on' : 'hint-btn coach-toggle'}
              aria-pressed={settings.coach}
              onClick={() => setSettings({ coach: !settings.coach })}
            >
              Coach
            </button>
          ) : null}
          {RankGuide ? (
            <button className="hint-btn" aria-haspopup="dialog" aria-expanded={guide} onClick={() => setGuide(true)}>
              Ranks
            </button>
          ) : null}
          <button className="hint-btn" onClick={() => setSorted((s) => !s)}>
            Sort
          </button>
        </div>
      </div>
      <Fan cards={cards} dealKey={view.hand?.fairness.commitment ?? null} onLeave />
      {RankGuide && guide ? (
        <ReferenceCard label="Card order" onClose={closeGuide}>
          <RankGuide view={view} />
        </ReferenceCard>
      ) : null}
      {nominating ? (
        <div className="sheet-scrim" onClick={() => setNominating(null)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-body">
              <h2>Name a suit for the Joker</h2>
              <p className="muted">Leading the Joker sets the suit everyone must follow.</p>
              <div className="option-row">
                {nominating.suits.map((suit) => (
                  <button
                    key={suit}
                    className={nominating.coach === suit ? 'btn btn-secondary coach-pick' : 'btn btn-secondary'}
                    onClick={() => {
                      play(nominating.card, suit)
                      setNominating(null)
                    }}
                  >
                    <SuitGlyph suit={suit} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** Whether it was your turn at the last look. Module state, because the
 *  auction and the play are different screens with different hand bands, and
 *  a band that mounted on your turn would otherwise buzz for a turn that was
 *  already announced. Null until a table has been seen at all. */
let wasMyTurn: boolean | null = null

/** One buzz when your turn comes round — to bid or to play. */
function useTurnBuzz(view: TableView): void {
  const mine = view.isMyTurn
  useEffect(() => {
    if (wasMyTurn === false && mine) buzz(BUZZ_TURN)
    wasMyTurn = mine
  }, [mine])
}

function SuitGlyph({ suit }: { suit: Suit }): JSX.Element {
  const glyph: Record<Suit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' }
  const cls: Record<Suit, string> = { S: 's-s', H: 's-h', D: 's-d', C: 's-c' }
  return <span className={`suit ${cls[suit]}`}>{glyph[suit]}</span>
}

/** Sorting is presentation, so the game module owns the order — it is the only
 *  thing that knows a left bower is a trump. The module always answers low to
 *  high, and `highLeft` reads that answer backwards rather than asking for a
 *  second ordering; that is the whole of the setting, in every game. */
function sortForDisplay(view: TableView, order: HandOrder): readonly CardId[] {
  const sorted = view.gameUi.sortHand ? view.gameUi.sortHand(view, view.myCards) : view.myCards
  return order === 'highLeft' ? [...sorted].reverse() : sorted
}
